import React, { useEffect, useState } from 'react'
import { flushSync } from 'react-dom'
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { StateInput } from '../shared/atom/inputs/StateInput'
import { CityInput } from '../shared/atom/inputs/CityInput'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import { Controller } from 'react-hook-form'
import { MpButton, MpTypography } from '../shared'
import MpTheme from 'shared/config/MpTheme'
import { MpReTheme } from '../../theme/OnboardingTheme'
import { Logger } from '@meprism/app-utils'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'
import { useProfileForm } from '../../utils/hooks/useProfileForm'
import { useAppDispatch } from '../../redux/storeExports'
import { putProfile, setupProfile } from 'shared/redux/peopleFinder/PeopleFinderSlice'
import { Grant } from 'shared/redux/product/ProductTypes'
import { PhoneNumberInput } from '../shared/atom/inputs/PhoneNumberInput'
import Loader from 'shared/components/atoms/Loader'
import MpLoadingIcon from '../shared/atom/MpLoadingIcon'
import { OnboardingStepper } from './OnboardingStepper'
import { autocompletePaperSx } from './OnboardingStep1Form'

const autofillSx = {
  '& input:-webkit-autofill': {
    WebkitBoxShadow: '0 0 0 1000px #1A1B20 inset !important',
    transition: 'background-color 5000s ease-in-out 0s',
  },
}

const labelSx = {
  color: '#EFEDFDB2',
  fontFamily: 'InterRegular',
  fontSize: '14px',
  mb: 0.5,
}


const MAX_EXTRA_ADDRESSES = 3

interface OnboardingStep2FormProps {
  grant?: Grant
  onBack?: () => void
}

export const OnboardingStep2Form = ({ grant, onBack }: OnboardingStep2FormProps) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [isCompleted, setIsCompleted] = useState(false)
  // Tracks only addresses the user explicitly added this session (not pre-loaded from profile)
  const [userAddedCount, setUserAddedCount] = useState(0)

  const {
    register,
    errors,
    isSubmitting,
    isValid,
    isLoading,
    control,
    handleSubmit,
    submitHandler,
    trigger,
    addressArray,
    watch,
    setValue,
  } = useProfileForm({
    linkedinOptional: true,
    streetOptional: !grant,
    zipOptional: !grant,
    phoneNumberOptional: !grant,
    agreeToTermsOptional: true,
    onSubmitSuccess: async () => {
      try {
        if (grant) {
          flushSync(() => setIsCompleted(true))
          navigate('/authorization')
        } else {
          dispatch(putProfile()).unwrap()
          dispatch(setupProfile()).unwrap()
          setIsCompleted(true)
          navigate('/dashboard')
        }
      } catch (error) {
        Logger.error(`Error setting up profile after step 2: ${error}`)
        toast.error('Your profile could not be processed')
      }
    },
    onSubmitError: (error) => {
      Logger.error(`Error submitting step 2: ${error}`)
      toast.error('Your profile could not be processed')
    },
  })
  const addressValues = watch('address')

  const handleStateChange = (index: number, stateCode: string) => {
    setValue(`address.${index}.state`, stateCode as any)
    setValue(`address.${index}.city`, '')
    trigger(`address.${index}.state`)
  }

  const handleCityChange = (index: number, cityName: string) => {
    setValue(`address.${index}.city`, cityName)
    trigger(`address.${index}.city`)
  }

  // When the async profile loads it may populate multiple saved addresses.
  // Remove any beyond what the user has manually added so they don't appear or get submitted.
  useEffect(() => {
    const expected = 1 + userAddedCount
    if (addressArray.fields.length > expected) {
      addressArray.remove(
        Array.from(
          { length: addressArray.fields.length - expected },
          (_, i) => expected + i,
        ),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressArray.fields.length])

  // The last visible address index (primary = 0, user additions = 1..userAddedCount)
  const lastVisibleIdx = userAddedCount
  const lastAddress = watch(`address.${lastVisibleIdx}`)
  const canAddMore =
    userAddedCount < MAX_EXTRA_ADDRESSES &&
    !!lastAddress?.streetAddress?.trim() &&
    !!lastAddress?.city?.trim() &&
    !!lastAddress?.state?.trim() &&
    !!lastAddress?.zip?.trim() &&
    !errors?.address?.[lastVisibleIdx]?.streetAddress &&
    !errors?.address?.[lastVisibleIdx]?.city &&
    !errors?.address?.[lastVisibleIdx]?.state &&
    !errors?.address?.[lastVisibleIdx]?.zip

  const handleAddAddress = () => {
    addressArray.append({ streetAddress: '', city: '', state: '' as any, zip: '' })
    setUserAddedCount((c) => c + 1)
  }

  const handleRemoveAddress = (index: number) => {
    addressArray.remove(index)
    setUserAddedCount((c) => c - 1)
  }

  if (isLoading) {
    return (
      <Box sx={{ mt: 16, display: 'flex', justifyContent: 'center' }}>
        <MpLoadingIcon positioned />
      </Box>
    )
  }

  return (
    <>
      <Box sx={{ textAlign: 'left', alignItems: 'center', px: 3, mt: 16, mb: 4, maxWidth: MpTheme.layouts.widths.sm, mx: 'auto' }}>
        <OnboardingStepper
          currentStep={isCompleted ? 3 : 2}
          onStepClick={!isCompleted ? (s) => { if (s === 1) onBack?.() } : undefined}
        />
      </Box>

      <Stack
        component="form"
        spacing={1}
        sx={{
          maxWidth: MpTheme.layouts.widths.sm,
          mx: 'auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          px: 3,
          mt: 2,
          '& .MuiFormLabel-asterisk': { color: 'red' },
          '& .MuiFormHelperText-root': { fontFamily: 'InterRegular', fontSize: '14px', letterSpacing: '0.02rem' },
        }}>
        <Stack spacing={1} sx={{ width: '100%' }}>

          {/* Additional First Name + Maiden Name */}
          <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={labelSx}>Additional First Name</Typography>
              <TextField
                fullWidth
                sx={autofillSx}
                placeholder="Enter your first name here"
                error={!!(errors?.otherFirstName?.[0] as any)?.value}
                helperText={(errors?.otherFirstName?.[0] as any)?.value?.message || ' '}
                FormHelperTextProps={{ sx: { marginLeft: 0, marginRight: 0 } }}
                {...register('otherFirstName.0.value', {
                  onBlur: () => trigger('otherFirstName.0.value'),
                })}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={labelSx}>Maiden Name</Typography>
              <TextField
                fullWidth
                sx={autofillSx}
                placeholder="Enter your maiden name here"
                error={!!(errors?.otherMaidenName?.[0] as any)?.value}
                helperText={(errors?.otherMaidenName?.[0] as any)?.value?.message || ' '}
                FormHelperTextProps={{ sx: { marginLeft: 0, marginRight: 0 } }}
                {...register('otherMaidenName.0.value', {
                  onBlur: () => trigger('otherMaidenName.0.value'),
                })}
              />
            </Box>
          </Box>

          {/* Primary address — full stacked layout, unchanged */}
          <Box sx={{ width: '100%' }}>
            <Typography sx={labelSx}>Street Address{!!grant && ' *'}</Typography>
            <TextField
              sx={{ width: '100%', ...autofillSx }}
              placeholder="Enter street address here"
              error={!!errors?.address?.[0]?.streetAddress}
              helperText={errors?.address?.[0]?.streetAddress?.message || ' '}
              FormHelperTextProps={{ sx: { marginLeft: 0, marginRight: 0 } }}
              {...register('address.0.streetAddress', {
                onChange: async (e) => { e.target.value = e.target.value.replace(/^\s+/, '').replace(/\s+/g, ' '); await trigger('address.0.streetAddress') },
                onBlur: async () => { await trigger('address.0.streetAddress') },
              })}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={labelSx}>State *</Typography>
              <Controller
                name="address.0.state"
                control={control}
                render={({ field: stateField }) => (
                  <StateInput
                    value={stateField.value}
                    onChange={(_e, newValue) => {
                      stateField.onChange(newValue);
                      handleStateChange(0, newValue);
                    }}
                    error={!!errors?.address?.[0]?.state}
                    helperText={errors?.address?.[0]?.state?.message || ' '}
                    placeholder="Enter state here"
                    inputSx={autofillSx}
                    paperSx={autocompletePaperSx}
                  />
                )}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={labelSx}>City *</Typography>
              <Controller
                name="address.0.city"
                control={control}
                render={({ field: cityField }) => (
                  <CityInput
                    value={cityField.value || ''}
                    stateCode={addressValues?.[0]?.state || addressArray.fields[0]?.state}
                    onChange={(_e, newValue) => {
                      cityField.onChange(newValue);
                      handleCityChange(0, newValue);
                    }}
                    error={!!errors?.address?.[0]?.city}
                    helperText={errors?.address?.[0]?.city?.message || ' '}
                    placeholder="Enter city here"
                    inputSx={autofillSx}
                    paperSx={autocompletePaperSx}
                  />
                )}
              />
            </Box>
          </Box>

          <Box sx={{ width: '100%' }}>
            <Typography sx={labelSx}>Zip Code{!!grant && ' *'}</Typography>
            <TextField
              sx={{ width: '100%', ...autofillSx }}
              placeholder="Enter zip code here"
              error={!!errors?.address?.[0]?.zip}
              helperText={errors?.address?.[0]?.zip?.message || ' '}
              FormHelperTextProps={{ sx: { marginLeft: 0, marginRight: 0 } }}
              inputProps={{ maxLength: 5 }}
              {...register('address.0.zip', {
                onChange: (e) => { e.target.value = e.target.value.replace(/\D/g, '').slice(0, 5) },
                onBlur: async () => { await trigger('address.0.zip') },
              })}
            />
          </Box>

          {/* User-added previous addresses — compact 2-row layout */}
          {Array.from({ length: userAddedCount }, (_, sliceIndex) => {
            const index = sliceIndex + 1
            const field = addressArray.fields[index]
            if (!field) return null
            return (
              <Box key={field.id} sx={{ width: '100%', mb: 5 }}>
                <Divider sx={{ mt: 1, mb: 1.5, borderColor: 'rgba(255, 255, 255, 0.15)' }} />
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography sx={{ color: '#ffffff', fontFamily: 'InterRegular', fontSize: '13px', pt:2, pb:2 }}>
                    Additional Address {sliceIndex + 1}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveAddress(index)}
                    sx={{ color: '#EFEDFDB2', p: 0.5, '&:hover': { color: '#ffffff' } }}>
                    <CloseIcon sx={{ fontSize: '16px' }} />
                  </IconButton>
                </Box>

                <TextField
                  fullWidth
                  size="small"
                  sx={autofillSx}
                  placeholder="Street address"
                  error={!!errors?.address?.[index]?.streetAddress}
                  helperText={errors?.address?.[index]?.streetAddress?.message || ' '}
                  FormHelperTextProps={{ sx: { marginLeft: 0, marginRight: 0 } }}
                  InputLabelProps={{ shrink: true }}
                  {...register(`address.${index}.streetAddress`, {
                    onChange: async (e) => { e.target.value = e.target.value.replace(/^\s+/, '').replace(/\s+/g, ' '); await trigger(`address.${index}.streetAddress`) },
                    onBlur: async () => { await trigger(`address.${index}.streetAddress`) },
                  })}
                />

                <Box sx={{ display: 'flex', gap: 1, width: '100%', mt: 2, mb:-0.5 }}>
                  <Box sx={{ flex: 1 }}>
                    <Controller
                      name={`address.${index}.state` as const}
                      control={control}
                      render={({ field: stateField }) => (
                        <StateInput
                          value={stateField.value}
                          onChange={(_e, newValue) => {
                            stateField.onChange(newValue);
                            handleStateChange(index, newValue);
                          }}
                          error={!!errors?.address?.[index]?.state}
                          helperText={errors?.address?.[index]?.state?.message || ' '}
                          placeholder="State"
                          size="small"
                          inputSx={autofillSx}
                          paperSx={autocompletePaperSx}
                        />
                      )}
                    />
                  </Box>
                  <Box sx={{ flex: 2 }}>
                    <Controller
                      name={`address.${index}.city` as const}
                      control={control}
                      render={({ field: cityField }) => (
                        <CityInput
                          value={cityField.value || ''}
                          stateCode={addressValues?.[index]?.state || addressArray.fields[index]?.state}
                          onChange={(_e, newValue) => {
                            cityField.onChange(newValue);
                            handleCityChange(index, newValue);
                          }}
                          error={!!errors?.address?.[index]?.city}
                          helperText={errors?.address?.[index]?.city?.message || ' '}
                          placeholder="City"
                          size="small"
                          inputSx={autofillSx}
                          paperSx={autocompletePaperSx}
                        />
                      )}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      sx={autofillSx}
                      placeholder="Zip"
                      error={!!errors?.address?.[index]?.zip}
                      helperText={errors?.address?.[index]?.zip?.message || ' '}
                      FormHelperTextProps={{ sx: { marginLeft: 0, marginRight: 0 } }}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ maxLength: 5 }}
                      {...register(`address.${index}.zip`, {
                        onChange: (e) => { e.target.value = e.target.value.replace(/\D/g, '').slice(0, 5) },
                        onBlur: async () => { await trigger(`address.${index}.zip`) },
                      })}
                    />
                  </Box>
                </Box>
              </Box>
            )
          })}

          {/* Add additional address button — subscribed users only */}
          {!!grant && userAddedCount < MAX_EXTRA_ADDRESSES && (
            <Box sx={{ width: '100%', pt: 0.5 }}>
              <Button
                onClick={canAddMore ? handleAddAddress : undefined}
                startIcon={<AddIcon sx={{ fontSize: '16px !important' }} />}
                sx={{
                  color: MpReTheme.colors.tertiary.pink.default,
                  textTransform: 'none',
                  fontFamily: 'InterRegular',
                  fontSize: '13px',
                  fontWeight: 'bold',
                  p: 1,
                  minWidth: 0,
                  background: 'none',
                  boxShadow: 'none',
                  pointerEvents: canAddMore ? 'auto' : 'none',
                  '&:hover': { background: 'none', boxShadow: 'none' },
                  mt:-1,
                  mb:2
                }}>
                Add additional address
              </Button>
            </Box>
          )}

          {/* Phone Number */}
          <Box sx={{ width: '100%' }}>
            <Typography sx={labelSx}>Phone Number{!!grant && ' *'}</Typography>
            <Controller
              control={control}
              name="phoneNumber.0.value"
              render={({ field, fieldState }) => (
                <PhoneNumberInput
                  context="onboarding"
                  value={field.value || ''}
                  onChange={(val: string) => field.onChange(val)}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message || ' '}
                />
              )}
            />
          </Box>

          {/* LinkedIn URL */}
          <Box sx={{ width: '100%' }}>
            <Typography sx={labelSx}>LinkedIn URL</Typography>
            <TextField
              sx={{ width: '100%', ...autofillSx }}
              placeholder="Enter linkedin url here"
              error={!!errors?.linkedIn}
              helperText={errors?.linkedIn?.message || ' '}
              FormHelperTextProps={{ sx: { marginLeft: 0, marginRight: 0 } }}
              {...register('linkedIn', {
                onChange: async () => { await trigger('linkedIn') },
                onBlur: async () => { await trigger('linkedIn') },
              })}
            />
          </Box>


        </Stack>

        {/* Terms */}
        <Stack spacing={2} sx={{ width: '100%' }}>
          <Box sx={{ flexDirection: 'row', display: 'flex', alignItems: 'flex-start' }}>
            <FormControl
              required
              error={!!errors.agreeToTerms}
              component="fieldset"
              variant="standard"
              sx={{ width: '100%', mt: -1, mb: 3 }}>
              <FormControlLabel
                control={
                  <Controller
                    name="agreeToTerms"
                    control={control}
                    rules={{ validate: (value) => value || 'You must agree to the terms and conditions' }}
                    render={({ field }) => (
                      <Checkbox
                        {...field}
                        checked={field.value}
                        onChange={(e) => { field.onChange(e); trigger('agreeToTerms') }}
                      />
                    )}
                  />
                }
                label={
                  <MpTypography variant="caption" sx={{ mt: 2,mb:2, textAlign: 'left' }}>
                    I have read and agree to the{' '}
                    <Link style={{ textDecoration: 'underline', fontSize: '16px' }} underline="hover" href="https://meprism.com/privacy-policy" target="_blank" rel="noopener noreferrer">
                      Privacy Policy
                    </Link>{' '}
                    and{' '}
                    <Link style={{ textDecoration: 'underline', fontSize: '16px' }} underline="hover" href="https://meprism.com/terms" target="_blank" rel="noopener noreferrer">
                      Terms of Use
                    </Link>.
                  </MpTypography>
                }
              />
              {errors.agreeToTerms && <FormHelperText>You must agree to the terms and conditions</FormHelperText>}
            </FormControl>
          </Box>

          <MpButton
            type="submit"
            variant="contained"
            disabled={isSubmitting || !isValid}
            size="large"
            color="darkPurple"
            sx={{ width: '100%' }}
            onClick={handleSubmit((values, event) => {
              values.metadata = {
                ...(values.metadata || {}),
                onboarding_step: 2,
                todo_actions: [],
              }
              if (!grant) {
                ;(event as any)._is_on_boarding_request = true
              }
              return submitHandler(values, event)
            })}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'center', whiteSpace: 'nowrap', width: '25%', '& .MuiCircularProgress-root': { color: '#5856d6 !important', width: '20px !important', height: '20px !important' } }}>
              <Box sx={{ flexGrow: 1, textAlign: 'center' }}>Complete Profile</Box>
              <Loader loading={isSubmitting} />
            </Box>
          </MpButton>
        </Stack>

      </Stack>
    </>
  )
}